<template>
  <div
    class="cmp-order-stepper row justify-center full-width ws-typography relative-position"
    :class="{ backdropped: editBackdropActive }"
    :data-test="testNames.stepper"
  >
    <div class="container full-width">
      <div
        class="row no-wrap ws-invisible-scrollbar"
        :class="{ 'justify-between': !usingAdminTheme, 'justify-evenly': usingAdminTheme }"
      >
        <template
          v-for="(step, index) in visibleSteps"
        >
          <div v-if="usingAdminTheme && index > 0" :key="index" class="divider"></div>
          <div
            :key="step"
            class="col col-auto ellipsis text-center"
          >
            <button
              class="nav-link composite-text ws-typo-button-sm"
              :class="{
                'enabled cursor-pointer': stepEnabled(step),
                'current': usingAdminTheme && isCurrentStep(step),
                'text-normal': usingAdminTheme
              }"
              :disabled="!stepEnabled(step)"
              :tabindex="0"
              @click="stepEnabled(step) && navigateToStep(step, false)"
              @keydown.enter="stepEnabled(step) && navigateToStep(step, false)"
            >
              <ws-icon
                v-if="usingAdminTheme && stepEnabled(step) && !isCurrentStep(step)"
                :name="Icon.SUCCESS"
                :size="24"
                color="green"
                class="q-mr-sm"
              ></ws-icon>
              <span v-else :class="{'circle q-mr-sm': usingAdminTheme}">
                {{ index + 1 }}<template v-if="!usingAdminTheme">.&nbsp;</template>
              </span>
              <span>
                {{ $t(getKey(step)) }}
              </span>
            </button>

            <div v-if="!usingAdminTheme" class="progress-wrap q-mt-xs q-mx-auto">
              <div
                class="progress"
                :style="'width: ' + progressByUser[step] + '%'"
              ></div>
              <div
                class="rest"
                :style="'width: ' + (100 - progressByUser[step]) + '%'"
              ></div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
import { Component, Vue } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';

import { getSnakedKeyFactory } from '@odin/utils';
import { Subscription, fromEvent } from 'rxjs';
import { throttle, debounceTime } from 'rxjs/operators';
import { WORKFLOW } from '@/consts/const.enum';
import { STEPS } from '@/services/const.enum';
import config from '@/services/config.service';
import { navigateToStep, stepEnabled, getCurrentStep } from '@/services/order.service';
import { Theme } from '@odin/services';
import {WsIcon} from '@odin/components-vue2';
import { Icon } from '@odin/assets';

const stepsHiddenInAdminTheme = [
  STEPS.BILLING_PROFILE,
  STEPS.DOMAIN_PROFILE,
  STEPS.PAYMENT_METHOD,
  STEPS.SERVICE_SETTINGS
];

export const testNames = process.env.NODE_ENV === 'test' ? {
  stepper: 'order-stepper',
} : {} as Record<string, string>;

@Component({
  components: {
    WsIcon,
  },
  computed: {
    ...mapState(['cart']),
    ...mapGetters(['progressByUser', 'orderSteps', 'editBackdropActive']),
  },
})
export default class OrderStepper extends Vue {
  orderSteps!: STEPS[];
  progressByUser!: Record<STEPS, number>;

  config = config;
  stepsKeyPrefix = 'cart.steps.';

  stepperTopOffset: number = 0;
  navElements: NodeListOf<Element> | undefined = undefined;
  observers: Array<MutationObserver> = [];

  resizeEvent: Subscription | null = null;

  Icon = Icon;
  testNames = testNames;
  data() {
    return {
      stepEnabled,
      getCurrentStep,
      navigateToStep,
    };
  }

  isCurrentStep(step: STEPS) {
    if (!this.usingAdminTheme) {
      return false;
    }

    if (step === STEPS.CART && stepsHiddenInAdminTheme.includes(getCurrentStep())) {
      return true;
    }

    return getCurrentStep() === step;
  }

  get usingAdminTheme() {
    return this.isTheme(Theme.ADMIN);
  }

  mounted() {
    this.setupStepper();
    const stream = fromEvent(window, 'resize');
    const controllerStream = stream.pipe(debounceTime(50));
    this.resizeEvent = stream
      .pipe(
        throttle(() => controllerStream, {
          leading: true,
          trailing: true,
        })
      )
      .subscribe(this.setStepperOffset);
  }

  //!FALBACK to be deleted after settlement about header class
  destroyed() {
    this.observers.forEach(obs => obs.disconnect());
    if (this.resizeEvent) {
      this.resizeEvent.unsubscribe();
    }
  }
  //------>end of fallback

  get visibleSteps() {
    if (this.isTheme(Theme.ADMIN)) {
      return this.orderSteps.filter(step =>
        !stepsHiddenInAdminTheme.includes(step)
      );
    }

    return this.orderSteps.filter(step =>
      config.workflow === WORKFLOW.SWEDISH
        ? step !== STEPS.BILLING_PROFILE
        : step !== STEPS.DOMAIN_PROFILE
    );
  }

  setupStepper() {
    //!FALBACK to be deleted after settlement about header class
    const el = document.querySelectorAll('.new-order-spa-header');
    if (!el.length) {
      this.hookFalbackClasses();
    }
    //------>end of fallback

    this.setStepperOffset();
  }

  setStepperOffset() {
    let topNavsHeight = 0;
    if (!this.navElements?.length) {
      return;
    }
    this.navElements.forEach((element: any) => {
      topNavsHeight += element.offsetHeight;
    });

    this.stepperTopOffset = topNavsHeight - 2; // minus 2px correction
  }

  getKey(key: string): string {
    this.getKey = getSnakedKeyFactory('cart.steps', (keys: string[]) => {
      if (keys[0] === 'domain-profile') {
        keys[0] = 'user_information';
      }
      return keys;
    });
    return this.getKey(key);
  }

  //!Fallback to be deleted after settlement about header class
  hookFalbackClasses() {
    const selectors =
      this.config.company === 'se-ws'
        ? '.header-fixed'
        : '.hf .menu__padding nav';
    this.navElements = document.querySelectorAll(selectors);
    for (const el of this.navElements) {
      const observer = new MutationObserver(this.setStepperOffset);
      observer.observe(el, { attributes: true });
      this.observers.push(observer);
    }
  }
  //------>end of fallback
}
</script>

<style lang="scss" scoped>
.cmp-order-stepper {
  z-index: 1;
  background: $order-stepper-bg;
  box-shadow: $ws-box-shadow7;

  .theme--admin & {
    box-shadow: unset;
    border-bottom: $ws-thin-line solid #D2D2D2;
  }

  > .container {
    max-width: $container-width;

    > .row {
      overflow-x: scroll;
      @media (max-width: $container-width) {
        padding-right: 20px;
      }

      > .divider {
        max-width: 90px;
        flex: 1;
        height: 1px;
        align-self: center;
        background: $ws-navy-60;
      }

      > .col {
        padding: map-get($space-lg, y) map-get($space-xs, x);
        &:first-of-type {
          padding-left: 0;
        }
        & + .col {
          margin-left: map-get($space-xl, x);
        }

        > .nav-link {
          display: inline-flex;
          align-items: center;

          border: none;
          background: none;

          font-family: $ws-font-family-secondary;
          color: $order-stepper-color;
          text-decoration: none;

          &:focus {
            border: none;
            outline: none;
          }

          &:focus-visible {
            outline: revert;
          }

          &.text-normal {
            font-weight: 400;
          }

          &.enabled {
            font-weight: bold;
          }

          &.current {
            color: $ws-king-200;
          }

          > .circle {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            border: 2px solid currentColor;
            background-color: transparent;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            user-select: none;
            font-size: 14px;
          }

          .theme--admin & {
            border: 0 transparent;
            background-color: unset;
          }
        }

        > .progress-wrap {
          display: flex;
          height: 3px;
          width: 80px;
          max-width: 100%;

          > .progress {
            background: $order-stepper-progress-bar-progress;
            height: 3px;
            width: 50%;
          }

          > .rest {
            background: $order-stepper-progress-bar-rest;
            height: 3px;
            width: 50%;
          }
        }

        @media (max-width: $ws-breakpoint-tablet-max) {
          padding-top: map-get($space-md, y);
          padding-bottom: map-get($space-md, y);
        }
      }

      .theme--cary & {
        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          pointer-events: none;
        }
      }
    }
  }

  &.backdropped:after {
    content: '';
    position: absolute;
    background: $order-stepper-backdrop-bg;
    opacity: $order-stepper-backdrop-opacity;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}
</style>
